import { RefObject, useEffect, useRef, useState } from 'react';
import { captureError } from '@leagueplatform/observability';
import { getDfdTargetOrigin } from 'common/dfd-embed-url';
import { getIdentityToken } from 'utils/identity-token';

export const DFD_IFRAME_READY = 'DFD_IFRAME_READY';
export const DFD_HOST_SET_TOKEN = 'DFD_HOST_SET_TOKEN';
export const DFD_IFRAME_REFRESH_TOKEN_REQUEST =
  'DFD_IFRAME_REFRESH_TOKEN_REQUEST';
export const DFD_IFRAME_NAVIGATE_REQUEST = 'DFD_IFRAME_NAVIGATE_REQUEST';

export interface DFDIframeReadyMessage {
  type: typeof DFD_IFRAME_READY;
}

export interface DFDHostSetTokenMessage {
  type: typeof DFD_HOST_SET_TOKEN;
  token: string;
}

export interface DFDIframeRefreshTokenRequestMessage {
  type: typeof DFD_IFRAME_REFRESH_TOKEN_REQUEST;
}

export interface DFDIframeNavigateRequestMessage {
  type: typeof DFD_IFRAME_NAVIGATE_REQUEST;
  url: string;
}

type DfdIframeMessage =
  | DFDIframeReadyMessage
  | DFDIframeRefreshTokenRequestMessage
  | DFDIframeNavigateRequestMessage;

/** Guest-session fallback window defined by the TELUS implementation guide. */
export const HANDSHAKE_WINDOW_MS = 2000;

type HandshakeFailure =
  | 'handshake-window-missed'
  | 'handshake-token-unavailable'
  | 'refresh-token-unavailable'
  | 'navigate-request-rejected';

export interface UseDfdIframeHandshakeOptions {
  iframeRef: RefObject<HTMLIFrameElement>;
  src: string;
  /** Names the host surface in failure reports. Carries no user data. */
  surface: string;
  /** Called with a URL the embed asked to move to, once it has been validated. */
  onNavigateRequest?: (url: string) => void;
}

export interface UseDfdIframeHandshakeResult {
  /** The iframe must not navigate before this is true. */
  isListening: boolean;
}

export const useDfdIframeHandshake = ({
  iframeRef,
  src,
  surface,
  onNavigateRequest,
}: UseDfdIframeHandshakeOptions): UseDfdIframeHandshakeResult => {
  const [isListening, setIsListening] = useState(false);

  const srcRef = useRef(src);
  srcRef.current = src;
  const surfaceRef = useRef(surface);
  surfaceRef.current = surface;
  const onNavigateRequestRef = useRef(onNavigateRequest);
  onNavigateRequestRef.current = onNavigateRequest;

  useEffect(() => {
    let isActive = true;
    let handshakeTimer: ReturnType<typeof setTimeout> | undefined;

    const report = (failure: HandshakeFailure) => {
      captureError(new Error(`DFD iframe handshake: ${failure}`), {
        context: { Context: { surface: surfaceRef.current, failure } },
      });
    };

    // Started at mount, not on the ready event: the token must be in hand before
    // the iframe announces itself.
    const tokenPromise = getIdentityToken()
      .getToken()
      .catch(() => null);

    const postToken = async (token: string) => {
      const contentWindow = iframeRef.current?.contentWindow;
      if (!contentWindow) return;

      const message: DFDHostSetTokenMessage = {
        type: DFD_HOST_SET_TOKEN,
        token,
      };
      contentWindow.postMessage(message, getDfdTargetOrigin(srcRef.current));
    };

    const isFromIframe = (event: MessageEvent) =>
      event.origin === getDfdTargetOrigin(srcRef.current) &&
      event.source === iframeRef.current?.contentWindow;

    const handleReady = async () => {
      let hasTimedOut = false;

      // Scoped to this ready event: the iframe can announce itself more than once,
      // and a shared reference would leave the earlier timer armed.
      const timer = setTimeout(() => {
        hasTimedOut = true;
        if (isActive) report('handshake-window-missed');
      }, HANDSHAKE_WINDOW_MS);
      handshakeTimer = timer;

      // The pre-warm is awaited for its cache, not its value: an iframe that
      // announces itself again needs a token current at that moment.
      await tokenPromise;
      const token = await getIdentityToken()
        .getToken()
        .catch(() => null);
      clearTimeout(timer);

      // Past the window the iframe is in a guest session and ignores tokens, so
      // there is nothing left to post and nothing further to report.
      if (!isActive || hasTimedOut) return;

      if (!token) {
        report('handshake-token-unavailable');
        return;
      }

      await postToken(token);
    };

    const handleRefreshRequest = async () => {
      const identityToken = getIdentityToken();
      let token: string | null = null;

      try {
        await identityToken.refreshToken();
        token = await identityToken.getToken();
      } catch {
        token = null;
      }

      if (!isActive) return;

      if (!token) {
        report('refresh-token-unavailable');
        return;
      }

      await postToken(token);
    };

    const isOnEmbedOrigin = (url: string) => {
      try {
        return new URL(url).origin === getDfdTargetOrigin(srcRef.current);
      } catch {
        return false;
      }
    };

    const handleNavigateRequest = ({
      url,
    }: DFDIframeNavigateRequestMessage) => {
      // The URL is attacker-influenced, so it is never reported and never
      // followed anywhere but the origin the iframe is already served from.
      if (typeof url !== 'string' || !isOnEmbedOrigin(url)) {
        report('navigate-request-rejected');
        return;
      }

      onNavigateRequestRef.current?.(url);
    };

    const handleMessage = (event: MessageEvent) => {
      if (!isFromIframe(event)) return;

      const data = event.data as DfdIframeMessage | undefined;
      if (!data) return;

      switch (data.type) {
        case DFD_IFRAME_READY:
          handleReady();
          break;
        case DFD_IFRAME_REFRESH_TOKEN_REQUEST:
          handleRefreshRequest();
          break;
        case DFD_IFRAME_NAVIGATE_REQUEST:
          handleNavigateRequest(data);
          break;
        default:
          break;
      }
    };

    window.addEventListener('message', handleMessage);
    setIsListening(true);

    return () => {
      isActive = false;
      clearTimeout(handshakeTimer);
      window.removeEventListener('message', handleMessage);
      setIsListening(false);
    };
    // src and surface are read through refs, so a change never leaves a stale
    // listener registered against the previous iframe.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [iframeRef]);

  return { isListening };
};
